import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ChevronDown, RefreshCw, ArrowDown, ArrowUp, Calendar, Check, FileDown } from 'lucide-react';

/**
 * HUPALUPA Store · The Zoo Family reklam raporu (/r/hupalupa-zoo-family).
 *
 * Overview2 "Marka Bazlı Harcama ve Ciro" tablosundaki Zoo Family satırının açılmış hâli:
 *   1. Özet           harcama, gösterim, tıklama, CTR, platform dönüşümü / ROAS
 *   2. Platformlar    Google · Meta · TikTok
 *   3. Aylık          harcama + GA4 Zoo Family cirosu
 *   4. Kampanyalar    Zoo Family'ye ayrılmış reklam grubu / varlık grubu / adset
 *   5. Ürünler        genel katalog kampanyalarında gösterilen Zoo Family ürünleri
 *   6. Satış          GA4'te Zoo Family ürünleri (reklam atfı DEĞİL)
 * Uç: GET /api/hupalupa/zoo-family (glowytics-backend/src/routes/hupalupaZooFamily.js).
 * Sadece HUPALUPA Store (App.js allowBrandIds). PDF: Europcar Kaynak Raporu'ndaki iframe + print yöntemi.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', up: '#047857', down: '#B91C1C'
};
const PLATFORM = {
  google: { label: 'Google Ads', renk: '#436AEB' },
  meta: { label: 'Meta Ads', renk: '#eb6834' },
  tiktok: { label: 'TikTok Ads', renk: '#1baf7a' }
};
const PLATFORMLAR = ['google', 'meta', 'tiktok'];

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const nf = (n) => Math.round(Number(n || 0)).toLocaleString('tr-TR');
const nf1 = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 });
const tl = (n) => `₺${nf(n)}`;
const tl2 = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `₺${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(v) >= 1e4) return `₺${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B`;
  return tl(v);
};
const sayiKisa = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}M`;
  if (v >= 1e4) return `${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B`;
  return nf(v);
};
const pct = (x, d = 0) => `%${(Number(x || 0) * 100).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const oran = (a, b) => (b > 0 ? a / b : null);
const ctrYazi = (v) => (v == null ? '—' : pct(v, 2));
const roasYazi = (v) => (v == null || !Number.isFinite(v) ? '—' : `${v.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}×`);
const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const AY_TAM = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const gunEtiket = (iso) => `${Number(iso.slice(8, 10))} ${AY[Number(iso.slice(5, 7)) - 1]}`;
const aralikEtiket = (a, b) => `${gunEtiket(a)}${a.slice(0, 4) !== b.slice(0, 4) ? ` ${a.slice(0, 4)}` : ''} – ${gunEtiket(b)} ${b.slice(0, 4)}`;
const ayTam = (ym) => `${AY_TAM[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

const iso = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

/* ---------------------------------------------------------------- tarih aralığı */

const ARALIKLAR = [
  { key: 'buyil', label: 'Bu yıl' },
  { key: 'buay', label: 'Bu ay' },
  { key: 'gecenay', label: 'Geçen ay' },
  { key: '30', label: 'Son 30 gün' },
  { key: '3ay', label: 'Son 3 ay' },
  { key: '6ay', label: 'Son 6 ay' },
  { key: '12ay', label: 'Son 12 ay' },
  { key: 'ozel', label: 'Özel aralık' }
];
function aralikOf(key) {
  const dun = new Date(); dun.setDate(dun.getDate() - 1);
  const ayBasi = (geri) => iso(new Date(dun.getFullYear(), dun.getMonth() - geri, 1));
  if (key === 'buay') return [ayBasi(0), iso(dun)];
  if (key === 'gecenay') {
    const b = new Date(); b.setDate(1); b.setMonth(b.getMonth() - 1);
    const s = new Date(); s.setDate(0);
    return [iso(b), iso(s)];
  }
  if (key === '30') { const d = new Date(dun); d.setDate(d.getDate() - 29); return [iso(d), iso(dun)]; }
  if (key === '3ay') return [ayBasi(2), iso(dun)];
  if (key === '6ay') return [ayBasi(5), iso(dun)];
  if (key === 'buyil') return [`${dun.getFullYear()}-01-01`, iso(dun)];
  if (key === '12ay') return [ayBasi(11), iso(dun)];
  return null;
}

/* ---------------------------------------------------------------- parçacıklar */

function Card({ children, style, className = '', id }) {
  return (
    <div id={id} className={className}
      style={{ background: C.card, borderRadius: 18, border: `1px solid ${C.hair}`, boxShadow: '0 1px 2px rgba(17,24,39,.04), 0 8px 24px rgba(17,24,39,.04)', ...style }}>
      {children}
    </div>
  );
}

function BolumBaslik({ id, eyebrow, title, sub, right }) {
  return (
    <div id={id} className="pdf-bolum" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginTop: 52, marginBottom: 16 }}>
      <div style={{ minWidth: 0 }}>
        {eyebrow ? <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>{eyebrow}</div> : null}
        <h2 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.022em', margin: '2px 0 0', lineHeight: 1.15, color: C.ink }}>{title}</h2>
        {sub ? <div style={{ fontSize: 15, color: C.muted, marginTop: 6, maxWidth: 820, lineHeight: 1.45 }}>{sub}</div> : null}
      </div>
      {right}
    </div>
  );
}

function Kpi({ label, value, lines, renk }) {
  return (
    <Card style={{ padding: '20px 20px 18px', position: 'relative', overflow: 'hidden' }}>
      {renk ? <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: renk }} /> : null}
      <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.025em', color: C.ink, marginTop: 8, lineHeight: 1.05 }} className="tabular-nums">
        {value}
      </div>
      <div style={{ marginTop: 10, display: 'grid', gap: 3 }}>
        {lines.filter(Boolean).map((l) => (
          <div key={l[0]} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: C.muted }} className="tabular-nums">
            <span>{l[0]}</span><span style={{ color: C.ink2, fontWeight: 600, whiteSpace: 'nowrap' }}>{l[1]}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PlatformRozet({ p }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: C.ink2, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: PLATFORM[p].renk }} />
      {PLATFORM[p].label}
    </span>
  );
}

const th = { padding: '10px 10px', fontWeight: 500, fontSize: 12, color: C.muted, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.line}` };
const td = { padding: '11px 10px', textAlign: 'right', whiteSpace: 'nowrap', borderTop: `1px solid ${C.hair}`, color: C.ink2 };
const tdTop = { ...td, fontWeight: 700, color: C.ink, borderTop: `2px solid ${C.line}`, background: '#FAFBFF' };

/** Ortak metrik sütunları — her tabloda aynı sırayla. */
const METRIK_KOLON = [
  { key: 'cost', label: 'Harcama', fmt: tl },
  { key: 'impressions', label: 'Gösterim', fmt: nf },
  { key: 'clicks', label: 'Tıklama', fmt: nf },
  { key: 'ctr', label: 'CTR', fmt: ctrYazi },
  { key: 'cpc', label: 'TBM', fmt: (v) => (v == null ? '—' : tl2(v)) },
  { key: 'conv', label: 'Dönüşüm', fmt: (v) => nf1(v) },
  { key: 'value', label: 'Dönüşüm değeri', fmt: tl },
  { key: 'roas', label: 'ROAS', fmt: roasYazi }
];
const turet = (x) => ({
  ...x,
  ctr: oran(x.clicks, x.impressions),
  cpc: oran(x.cost, x.clicks),
  roas: oran(x.value, x.cost)
});
const topla = (rows) => rows.reduce((a, r) => ({
  cost: a.cost + (r.cost || 0), impressions: a.impressions + (r.impressions || 0), clicks: a.clicks + (r.clicks || 0),
  conv: a.conv + (r.conv || 0), value: a.value + (r.value || 0)
}), { cost: 0, impressions: 0, clicks: 0, conv: 0, value: 0 });

function SiraliBaslik({ kolonlar, sira, setSira, ilk, ilkGenislik }) {
  const sirala = (key) => setSira((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: true }));
  return (
    <thead>
      <tr>
        {ilk.map((h, i) => (
          <th key={h} style={{ ...th, textAlign: 'left', paddingLeft: i === 0 ? 24 : 10, width: i === 0 ? ilkGenislik : undefined }}>{h}</th>
        ))}
        {kolonlar.map((c, i) => (
          <th key={c.key} onClick={() => sirala(c.key)}
            style={{ ...th, cursor: 'pointer', userSelect: 'none', color: sira.key === c.key ? C.ink : C.muted, fontWeight: sira.key === c.key ? 600 : 500, paddingRight: i === kolonlar.length - 1 ? 24 : 10 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              {c.label}
              {sira.key === c.key ? (sira.desc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : null}
            </span>
          </th>
        ))}
      </tr>
    </thead>
  );
}

const siraUygula = (rows, sira) => [...rows].sort((a, b) => {
  const av = a[sira.key] ?? -Infinity; const bv = b[sira.key] ?? -Infinity;
  return (sira.desc ? bv - av : av - bv) || b.cost - a.cost;
});

function Bos({ loading, yazi }) {
  return <div style={{ padding: 24, fontSize: 14, color: C.muted }}>{loading ? 'Yükleniyor…' : yazi}</div>;
}

function Dipnot({ children }) {
  return <div style={{ fontSize: 12, color: C.faint, padding: '12px 24px 10px', lineHeight: 1.6 }}>{children}</div>;
}

/* ---------------------------------------------------------------- platformlar */

function PlatformTablosu({ data }) {
  const rows = PLATFORMLAR.map((p) => turet({ p, ...(data?.platforms?.[p] || {}) })).filter((r) => r.cost > 0 || r.impressions > 0);
  const top = turet(topla(rows));
  return (
    <Card style={{ padding: '8px 0 4px' }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 14, minWidth: 980 }} className="tabular-nums">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Platform</th>
              <th style={{ ...th, textAlign: 'left' }}>Harcama payı</th>
              {METRIK_KOLON.map((c, i) => <th key={c.key} style={{ ...th, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10 }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.p}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24 }}><PlatformRozet p={r.p} /></td>
                <td style={{ ...td, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 90, height: 6, background: C.hair, borderRadius: 3 }}>
                      <div style={{ width: `${top.cost ? (r.cost / top.cost) * 100 : 0}%`, height: 6, borderRadius: 3, background: PLATFORM[r.p].renk }} />
                    </div>
                    <span style={{ minWidth: 34 }}>{top.cost ? pct(r.cost / top.cost) : '—'}</span>
                  </div>
                </td>
                {METRIK_KOLON.map((c, i) => (
                  <td key={c.key} style={{ ...td, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10, fontWeight: c.key === 'cost' || c.key === 'roas' ? 600 : 400, color: c.key === 'cost' || c.key === 'roas' ? C.ink : C.ink2 }}>
                    {c.fmt(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Toplam</td>
              <td style={{ ...tdTop, textAlign: 'left' }}>%100</td>
              {METRIK_KOLON.map((c, i) => <td key={c.key} style={{ ...tdTop, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10 }}>{c.fmt(top[c.key])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        Dönüşüm ve dönüşüm değeri her platformun kendi raporladığı satın almadır; ROAS = dönüşüm değeri / harcama.
        TikTok yalnız trafik kampanyasında koştu (satın alma ölçülmez) ve TikTok'un "tıklama" tanımı geniş — CTR'si Google/Meta ile kıyaslanmaz.
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- aylık */

function AylikGrafik({ months }) {
  const max = Math.max(1, ...months.map((m) => m.total.cost));
  const H = 180;
  return (
    <div className="pdf-blok" style={{ padding: '22px 24px 8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>Aylık harcama</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {PLATFORMLAR.map((p) => <PlatformRozet key={p} p={p} />)}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: H + 46, marginTop: 14 }}>
        {months.map((m) => (
          <div key={m.month} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.ink2, marginBottom: 6, whiteSpace: 'nowrap' }} className="tabular-nums">
              {m.total.cost ? tlShort(m.total.cost) : ''}
            </div>
            <div style={{ width: '100%', maxWidth: 64, display: 'flex', flexDirection: 'column-reverse', borderRadius: 6, overflow: 'hidden', height: (m.total.cost / max) * H, minHeight: m.total.cost ? 2 : 0 }}>
              {PLATFORMLAR.map((p) => (m[p].cost > 0 ? (
                <div key={p} title={`${PLATFORM[p].label}: ${tl(m[p].cost)}`} style={{ height: `${(m[p].cost / m.total.cost) * 100}%`, background: PLATFORM[p].renk }} />
              ) : null))}
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{AY[Number(m.month.slice(5, 7)) - 1]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AylikTablo({ data, loading }) {
  const months = data?.months || [];
  if (!months.length) return <Card><Bos loading={loading} yazi="Bu aralıkta veri yok." /></Card>;
  const satirlar = months.map((m) => ({ ...turet(m.total), month: m.month, ga4Revenue: m.ga4Revenue || 0, ga4Qty: m.ga4Qty || 0, by: m }));
  const top = { ...turet(topla(satirlar.map((s) => s))), ga4Revenue: satirlar.reduce((a, s) => a + s.ga4Revenue, 0), ga4Qty: satirlar.reduce((a, s) => a + s.ga4Qty, 0) };
  const kolon = [
    ...PLATFORMLAR.map((p) => ({ key: p, label: PLATFORM[p].label, v: (s) => s.by?.[p]?.cost, fmt: (v) => (v ? tl(v) : '—'), renk: PLATFORM[p].renk })),
    ...METRIK_KOLON.map((c) => ({ key: c.key, label: c.key === 'cost' ? 'Toplam harcama' : c.label, v: (s) => s[c.key], fmt: c.fmt, kalin: c.key === 'cost' || c.key === 'roas' })),
    { key: 'ga4', label: 'GA4 Zoo Family cirosu', v: (s) => s.ga4Revenue, fmt: (v) => (v ? tl(v) : '—'), ayri: true },
    { key: 'ga4q', label: 'Adet', v: (s) => s.ga4Qty, fmt: (v) => (v ? nf(v) : '—') }
  ];
  const topDeger = (k) => (k.key === 'ga4' ? top.ga4Revenue : k.key === 'ga4q' ? top.ga4Qty : PLATFORMLAR.includes(k.key) ? data.platforms[k.key].cost : top[k.key]);
  return (
    <Card style={{ padding: 0, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
      <AylikGrafik months={months} />
      <div className="overflow-x-auto" style={{ marginTop: 6 }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1400 }} className="tabular-nums">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Ay</th>
              {kolon.map((k, i) => (
                <th key={k.key} style={{ ...th, paddingRight: i === kolon.length - 1 ? 24 : 10, borderLeft: k.ayri ? `1px solid ${C.line}` : undefined }}>
                  {k.renk ? <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, background: k.renk, marginRight: 5 }} /> : null}{k.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {satirlar.map((s) => (
              <tr key={s.month}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, fontWeight: 600, color: C.ink }}>{ayTam(s.month)}</td>
                {kolon.map((k, i) => (
                  <td key={k.key} style={{ ...td, paddingRight: i === kolon.length - 1 ? 24 : 10, fontWeight: k.kalin ? 600 : 400, color: k.kalin ? C.ink : C.ink2, borderLeft: k.ayri ? `1px solid ${C.line}` : undefined }}>
                    {k.fmt(k.v(s))}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Toplam</td>
              {kolon.map((k, i) => (
                <td key={k.key} style={{ ...tdTop, paddingRight: i === kolon.length - 1 ? 24 : 10, borderLeft: k.ayri ? `1px solid ${C.line}` : undefined }}>{k.fmt(topDeger(k))}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        GA4 cirosu sitenin o ay sattığı tüm Zoo Family ürünleridir (GA4 itemBrand); reklamdan gelen satış değildir, ROAS hesabına girmez.
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- kampanyalar */

function KampanyaTablosu({ data, loading }) {
  const [sira, setSira] = useState({ key: 'cost', desc: true });
  const rows = useMemo(() => siraUygula((data?.nodes || []).map(turet), sira), [data, sira]);
  if (!rows.length) return <Card><Bos loading={loading} yazi="Bu aralıkta Zoo Family'ye ayrılmış kampanya yok." /></Card>;
  const top = turet(topla(rows));
  return (
    <Card style={{ padding: '8px 0 4px', opacity: loading ? 0.55 : 1 }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1360 }} className="tabular-nums">
          <SiraliBaslik kolonlar={METRIK_KOLON} sira={sira} setSira={setSira} ilk={['Kampanya › reklam grubu', 'Yayın']} ilkGenislik={400} />
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.platform}|${r.campaign}|${r.node}`}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, whiteSpace: 'normal' }}>
                  <div style={{ width: 400 }}>
                    <div style={{ fontWeight: 600, color: C.ink, lineHeight: 1.35, wordBreak: 'break-word' }}>{r.node}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <PlatformRozet p={r.platform} />
                      <span>{r.tip}</span>
                      <span style={{ color: C.faint }}>· {r.campaign}</span>
                    </div>
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'left', fontSize: 12.5, color: C.muted }}>{aralikEtiket(r.first, r.last)}</td>
                {METRIK_KOLON.map((c, i) => (
                  <td key={c.key} style={{ ...td, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10, fontWeight: c.key === 'cost' || c.key === 'roas' ? 600 : 400, color: c.key === 'cost' || c.key === 'roas' ? C.ink : C.ink2 }}>
                    {c.fmt(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Toplam <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· {rows.length} satır</span></td>
              <td style={tdTop} />
              {METRIK_KOLON.map((c, i) => <td key={c.key} style={{ ...tdTop, paddingRight: i === METRIK_KOLON.length - 1 ? 24 : 10 }}>{c.fmt(top[c.key])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        Harcama en ince düğümden okunur: Google reklam grubu ve PMax varlık grubu, Meta adset, TikTok reklam grubu. Düğüm adı Zoo Family demiyorsa
        kampanya adına bakılır (örn. Glowy_Search_ZooFamily › Ad group 1). Overview2'deki marka tablosuyla aynı kural, aynı toplam.
        {data?.excluded?.length ? (
          <> Adında Zoo Family geçip başka markaya yazılanlar: {data.excluded.map((e) => `${e.campaign} › ${e.node} (${tl(e.cost)}, ${e.brand || 'markasız'})`).join('; ')} — reklamın kendisi o markayı gösteriyor.</>
        ) : null}
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- ürünler */

const URUN_KOLON = METRIK_KOLON.filter((c) => c.key !== 'cpc');

function UrunTablosu({ data, loading }) {
  const [sira, setSira] = useState({ key: 'cost', desc: true });
  const [hepsi, setHepsi] = useState(false);
  const pr = data?.products;
  const rows = useMemo(() => siraUygula((pr?.items || []).map(turet), sira), [pr, sira]);
  if (!rows.length) {
    return <Card><Bos loading={loading} yazi={pr?.googleError ? `Google ürün verisi okunamadı: ${pr.googleError}` : 'Bu aralıkta ürün verisi yok.'} /></Card>;
  }
  const top = turet(topla(rows));
  const gorunen = hepsi ? rows : rows.slice(0, 20);
  return (
    <Card style={{ padding: '8px 0 4px', opacity: loading ? 0.55 : 1 }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1200 }} className="tabular-nums">
          <SiraliBaslik kolonlar={URUN_KOLON} sira={sira} setSira={setSira} ilk={['Ürün']} ilkGenislik={460} />
          <tbody>
            {gorunen.map((r) => (
              <tr key={`${r.platform}|${r.itemId}`}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, whiteSpace: 'normal' }}>
                  <div style={{ width: 460 }}>
                    <div style={{ fontWeight: 600, color: C.ink, lineHeight: 1.35 }}>{r.title || r.itemId}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <PlatformRozet p={r.platform} />
                      {r.extraCost > 0 ? <span>· genel kampanyalarda {tl(r.extraCost)}</span> : null}
                    </div>
                  </div>
                </td>
                {URUN_KOLON.map((c, i) => (
                  <td key={c.key} style={{ ...td, paddingRight: i === URUN_KOLON.length - 1 ? 24 : 10, fontWeight: c.key === 'cost' ? 600 : 400, color: c.key === 'cost' ? C.ink : C.ink2 }}>
                    {r.platform === 'meta' && (c.key === 'conv' || c.key === 'value' || c.key === 'roas') ? '—' : c.fmt(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Toplam <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· {rows.length} ürün</span></td>
              {URUN_KOLON.map((c, i) => <td key={c.key} style={{ ...tdTop, paddingRight: i === URUN_KOLON.length - 1 ? 24 : 10 }}>{c.fmt(top[c.key])}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {rows.length > 20 ? (
        <div className="pdf-gizle" style={{ padding: '10px 24px 0' }}>
          <button type="button" onClick={() => setHepsi((v) => !v)}
            style={{ border: 0, background: 'transparent', color: C.accent, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', padding: 0 }}>
            {hepsi ? 'Daha az göster' : `Tüm ${rows.length} ürünü göster`}
          </button>
        </div>
      ) : null}
      <Dipnot>
        Google: alışveriş ağındaki ürün raporu (PMax'in alışveriş payı + Standart Alışveriş), canlı Google Ads API. Meta: katalog reklamlarının ürün kırılımı
        (9 Haziran 2026'dan beri tutuluyor); Meta ürün düzeyinde satın alma vermediği için dönüşüm sütunları boş.
        PMax "Zoo Family Kamera" varlık grubunun alışveriş gösterimleri bu tabloda da görünür; bu yüzden ürün toplamı yukarıdaki kampanya toplamına eklenmez.
        Yalnız Zoo Family'ye ayrılmamış genel kampanyalardaki pay ("genel kampanyalarda") ek harcamadır.
        {pr?.googleError ? ` Google ürün verisi okunamadı: ${pr.googleError}` : ''}
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- GA4 satış */

function SatisTablosu({ data, loading }) {
  const items = data?.ga4?.items || [];
  if (!items.length) return <Card><Bos loading={loading} yazi={data?.ga4Error ? `GA4 okunamadı: ${data.ga4Error}` : 'Bu aralıkta Zoo Family satışı yok.'} /></Card>;
  const top = items.reduce((a, r) => ({ views: a.views + r.views, carts: a.carts + r.carts, qty: a.qty + r.qty, revenue: a.revenue + r.revenue }), { views: 0, carts: 0, qty: 0, revenue: 0 });
  const max = Math.max(1, ...items.map((r) => r.revenue));
  const gorunen = items.slice(0, 15);
  const kalan = items.slice(15);
  const kolonlar = [
    ['views', 'Ürün görüntüleme', nf], ['carts', 'Sepete ekleme', nf], ['sepetOrani', 'Sepete ekleme oranı', (v) => (v == null ? '—' : pct(v, 1))],
    ['qty', 'Satılan adet', nf], ['revenue', 'Ciro', tl], ['pay', 'Ciro payı', (v) => (v == null ? '—' : pct(v, 1))]
  ];
  const tur = (r) => ({ ...r, sepetOrani: oran(r.carts, r.views), pay: oran(r.revenue, top.revenue) });
  return (
    <Card style={{ padding: '8px 0 4px', opacity: loading ? 0.55 : 1 }}>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5, minWidth: 1100 }} className="tabular-nums">
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', paddingLeft: 24 }}>Ürün</th>
              {kolonlar.map(([k, l], i) => <th key={k} style={{ ...th, paddingRight: i === kolonlar.length - 1 ? 24 : 10 }}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {[...gorunen, ...(kalan.length ? [{ name: `Diğer ${kalan.length} ürün`, diger: true, ...kalan.reduce((a, r) => ({ views: a.views + r.views, carts: a.carts + r.carts, qty: a.qty + r.qty, revenue: a.revenue + r.revenue }), { views: 0, carts: 0, qty: 0, revenue: 0 }) }] : [])].map(tur).map((r) => (
              <tr key={r.name}>
                <td style={{ ...td, textAlign: 'left', paddingLeft: 24, whiteSpace: 'normal' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: 520 }}>
                    <span style={{ flex: 1, fontWeight: r.diger ? 500 : 600, color: r.diger ? C.muted : C.ink, lineHeight: 1.35 }}>{r.name}</span>
                    <div style={{ width: 70, height: 6, background: C.hair, borderRadius: 3, flexShrink: 0 }}>
                      <div style={{ width: `${(r.revenue / max) * 100}%`, maxWidth: '100%', height: 6, borderRadius: 3, background: C.accent }} />
                    </div>
                  </div>
                </td>
                {kolonlar.map(([k, , f], i) => (
                  <td key={k} style={{ ...td, paddingRight: i === kolonlar.length - 1 ? 24 : 10, fontWeight: k === 'revenue' ? 600 : 400, color: k === 'revenue' ? C.ink : C.ink2 }}>{f(r[k])}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td style={{ ...tdTop, textAlign: 'left', paddingLeft: 24 }}>Toplam <span style={{ color: C.faint, fontWeight: 500, fontSize: 12.5 }}>· {items.length} ürün</span></td>
              {kolonlar.map(([k, , f], i) => (
                <td key={k} style={{ ...tdTop, paddingRight: i === kolonlar.length - 1 ? 24 : 10 }}>{f(tur(top)[k])}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <Dipnot>
        GA4 e-ticaret (itemBrand "The Zoo Family"): sitenin bu aralıkta sattığı tüm Zoo Family ürünleri, hangi kanaldan gelirse gelsin. Reklam atfı değildir.
      </Dipnot>
    </Card>
  );
}

/* ---------------------------------------------------------------- tarih seçici */

function TarihSecici({ araKey, secAralik, ozel, setOzel, uygulaOzel, range, loading, onYenile }) {
  const [acik, setAcik] = useState(false);
  const kutu = useRef(null);
  const dun = aralikOf('30')[1];

  useEffect(() => {
    if (!acik) return undefined;
    const disari = (e) => { if (kutu.current && !kutu.current.contains(e.target)) setAcik(false); };
    const esc = (e) => { if (e.key === 'Escape') setAcik(false); };
    document.addEventListener('mousedown', disari);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', disari); document.removeEventListener('keydown', esc); };
  }, [acik]);

  const etiket = ARALIKLAR.find((a) => a.key === araKey)?.label || 'Tarih aralığı';
  const kisaAralik = (r) => (r ? aralikEtiket(r[0], r[1]) : '');
  const tarihInput = { width: '100%', minWidth: 0, boxSizing: 'border-box', border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 6px', background: '#fff', fontSize: 13, color: C.ink, fontFamily: FONT };

  return (
    <div ref={kutu} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => setAcik((v) => !v)} aria-expanded={acik}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer', fontFamily: FONT,
            background: '#fff', border: `1px solid ${acik ? C.accent : C.line}`, borderRadius: 12, padding: '9px 12px',
            boxShadow: acik ? `0 0 0 3px ${C.accentSoft}` : '0 1px 2px rgba(17,24,39,.05)', transition: 'border-color .15s, box-shadow .15s' }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: C.accentSoft, color: C.accent, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Calendar size={15} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: C.ink, lineHeight: 1.2 }}>{etiket}</span>
            <span style={{ display: 'block', fontSize: 12, color: C.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="tabular-nums">
              {kisaAralik(range)}
            </span>
          </span>
          <ChevronDown size={16} style={{ color: C.faint, flexShrink: 0, transform: acik ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>
        <button type="button" onClick={onYenile} title="Veriyi yenile"
          style={{ border: `1px solid ${C.line}`, background: '#fff', borderRadius: 12, width: 50, display: 'grid', placeItems: 'center', cursor: 'pointer', color: C.ink2, flexShrink: 0 }}>
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {acik ? (
        <div role="listbox"
          style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0, zIndex: 30, background: 'rgba(255,255,255,.98)', backdropFilter: 'blur(12px)',
            border: `1px solid ${C.line}`, borderRadius: 14, boxShadow: '0 12px 40px rgba(17,24,39,.14), 0 2px 6px rgba(17,24,39,.06)', padding: 6 }}>
          {ARALIKLAR.filter((a) => a.key !== 'ozel').map((a) => {
            const on = araKey === a.key;
            return (
              <button key={a.key} type="button" role="option" aria-selected={on}
                onClick={() => { secAralik(a.key); setAcik(false); }}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = '#F3F4F8'; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
                style={{ width: '100%', border: 0, cursor: 'pointer', borderRadius: 9, padding: '8px 10px', fontFamily: FONT, textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8, background: on ? C.accentSoft : 'transparent' }}>
                <span style={{ width: 14, color: C.accent, display: 'grid', placeItems: 'center' }}>{on ? <Check size={14} strokeWidth={2.5} /> : null}</span>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? C.accent : C.ink, whiteSpace: 'nowrap' }}>{a.label}</span>
                <span style={{ fontSize: 11.5, color: C.faint, whiteSpace: 'nowrap' }} className="tabular-nums">{kisaAralik(aralikOf(a.key))}</span>
              </button>
            );
          })}
          <div style={{ height: 1, background: C.hair, margin: '6px 4px' }} />
          <div style={{ padding: '6px 10px 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: araKey === 'ozel' ? 600 : 500, color: araKey === 'ozel' ? C.accent : C.ink }}>
              <span style={{ width: 14, display: 'grid', placeItems: 'center' }}>{araKey === 'ozel' ? <Check size={14} strokeWidth={2.5} /> : null}</span>
              Özel aralık
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6, marginTop: 8 }}>
              <input type="date" aria-label="Başlangıç" value={ozel[0]} min="2025-01-01" max={ozel[1]} onChange={(e) => setOzel([e.target.value, ozel[1]])} style={tarihInput} />
              <input type="date" aria-label="Bitiş" value={ozel[1]} min={ozel[0]} max={dun} onChange={(e) => setOzel([ozel[0], e.target.value])} style={tarihInput} />
            </div>
            <button type="button" onClick={() => { secAralik('ozel'); uygulaOzel(); setAcik(false); }}
              style={{ width: '100%', marginTop: 8, border: 0, background: C.accent, color: '#fff', borderRadius: 9, padding: '8px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              Uygula
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- PDF */

/**
 * PDF: sayfanın o anki görünümü gizli bir iframe'e kopyalanır ve orada yazdırılır (tarayıcının
 * "PDF olarak kaydet"i). Metin vektör kalır, tablo başlıkları her sayfada tekrarlanır.
 * Baskıda medya sorguları kağıt genişliğine göre çalışır → ızgaralar .pdf-* ile sabitlenir.
 * zoom 0.66: ~1560px düzen yatay A4'ün yazdırılabilir alanına sığar.
 */
const PDF_CSS = `
@page { size: A4 landscape; margin: 9mm 8mm; }
html, body { margin: 0; background: ${C.ground}; }
body { zoom: 0.66; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pdf-kok { width: 1560px; padding: 8px 4px; font-family: ${FONT}; color: ${C.ink}; }
.pdf-gizle { display: none !important; }
.pdf-goster { display: block !important; }
.pdf-kpi { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; }
.overflow-auto, .overflow-x-auto { max-height: none !important; overflow: visible !important; opacity: 1 !important; }
table { min-width: 0 !important; }
thead { display: table-header-group; }
th, td, thead { position: static !important; }
tr, .pdf-blok { break-inside: avoid; page-break-inside: avoid; }
h1, h2, .pdf-bolum { break-after: avoid; page-break-after: avoid; }
* { box-shadow: none !important; transition: none !important; animation: none !important; }
`;

function pdfIndir(kaynak, baslik) {
  if (!kaynak) return;
  const eski = document.getElementById('zoo-pdf-iframe');
  if (eski) eski.remove();
  const iframe = document.createElement('iframe');
  iframe.id = 'zoo-pdf-iframe';
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '1600px', height: '900px', border: '0', opacity: '0', pointerEvents: 'none', zIndex: '-1' });
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const stiller = [...document.querySelectorAll('style, link[rel="stylesheet"]')].map((n) => n.outerHTML).join('\n');
  const kok = kaynak.cloneNode(true);
  kok.removeAttribute('style');
  kok.className = 'pdf-kok';
  const olusturma = new Date().toLocaleString('tr-TR', { dateStyle: 'long', timeStyle: 'short' });
  doc.open();
  doc.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${baslik.replace(/</g, '')}</title>${stiller}<style>${PDF_CSS}</style></head>
    <body>${kok.outerHTML}<div style="font: 12px ${FONT}; color: ${C.faint}; padding: 12px 4px 0;">Glowytics · ${olusturma} tarihinde oluşturuldu</div></body></html>`);
  doc.close();

  let basildi = false;
  const yazdir = () => {
    if (basildi) return;
    basildi = true;
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => iframe.remove(), 1000);
    }
  };
  const linkler = [...doc.querySelectorAll('link[rel="stylesheet"]')];
  if (!linkler.length) { setTimeout(yazdir, 150); return; }
  let kalan = linkler.length;
  const bitti = () => { kalan -= 1; if (kalan <= 0) setTimeout(yazdir, 150); };
  linkler.forEach((l) => { l.addEventListener('load', bitti); l.addEventListener('error', bitti); });
  setTimeout(yazdir, 3000);
}

/* ---------------------------------------------------------------- sayfa */

export default function HupalupaZooFamilyRaporu() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [araKey, setAraKey] = useState('buyil');
  const [range, setRange] = useState(() => aralikOf('buyil'));
  const [ozel, setOzel] = useState(() => aralikOf('buyil'));
  const [refreshTick, setRefreshTick] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const raporRef = useRef(null);

  const secAralik = (k) => {
    setAraKey(k);
    if (k === 'ozel') return;
    const r = aralikOf(k);
    setRange(r); setOzel(r);
  };
  const uygulaOzel = () => setRange(ozel);

  const getir = useCallback(async (r, refresh) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/hupalupa/zoo-family?startDate=${r[0]}&endDate=${r[1]}${refresh ? '&refresh=1' : ''}`, { credentials: 'include', headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Rapor yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (brandId) getir(range, refreshTick > 0);
  }, [brandId, range, getir, refreshTick]);

  const t = turet(data?.totals || { cost: 0, impressions: 0, clicks: 0, conv: 0, value: 0 });
  const p = data?.platforms || {};
  const gm = turet(topla([p.google || {}, p.meta || {}]));
  const ga4 = data?.ga4;
  const ek = data?.products?.extra;
  const ekHarcama = (ek?.google?.cost || 0) + (ek?.meta?.cost || 0);
  const aktifPlatform = PLATFORMLAR.filter((k) => p[k]?.cost > 0);

  return (
    <div style={{ background: C.ground, minHeight: '100%', fontFamily: FONT, color: C.ink }} className="px-4 sm:px-8 py-8">
      <div ref={raporRef} style={{ maxWidth: 1640, margin: '0 auto' }}>
        {/* Başlık */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.accent }}>HUPALUPA Store</div>
            <h1 style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', margin: '4px 0 0', lineHeight: 1.05 }}>The Zoo Family reklam raporu</h1>
            <div style={{ fontSize: 17, color: C.muted, marginTop: 10, lineHeight: 1.45 }}>
              {aralikEtiket(range[0], range[1])} · Zoo Family ürünlerinin yer aldığı Google, Meta ve TikTok reklamları
            </div>
          </div>
          <div className="pdf-gizle" style={{ display: 'flex', gap: 8, width: 420, maxWidth: '100%', position: 'relative', zIndex: 20 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TarihSecici araKey={araKey} secAralik={secAralik} ozel={ozel} setOzel={setOzel} uygulaOzel={uygulaOzel}
                range={range} loading={loading} onYenile={() => setRefreshTick((x) => x + 1)} />
            </div>
            <button type="button" onClick={() => pdfIndir(raporRef.current, `HUPALUPA Store - The Zoo Family ${aralikEtiket(range[0], range[1])}`)}
              disabled={loading || !data} title={loading ? 'Veri yükleniyor…' : 'Sayfayı PDF olarak kaydet'}
              style={{ display: 'flex', alignItems: 'center', gap: 7, border: 0, borderRadius: 12, padding: '0 16px', background: C.ink, color: '#fff',
                fontSize: 13.5, fontWeight: 600, fontFamily: FONT, cursor: loading ? 'wait' : 'pointer', opacity: loading || !data ? 0.5 : 1, flexShrink: 0 }}>
              <FileDown size={16} /> PDF
            </button>
          </div>
        </div>

        {/* Özet */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 pdf-kpi" style={{ gap: 14, marginTop: 28, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <Kpi label="Harcama" value={tlShort(t.cost)}
            lines={aktifPlatform.map((k) => [PLATFORM[k].label, tl(p[k].cost)])} />
          <Kpi label="Gösterim" value={sayiKisa(t.impressions)}
            lines={[['BGBM', t.impressions ? tl2((t.cost / t.impressions) * 1000) : '—'], ['Toplam', nf(t.impressions)]]} />
          <Kpi label="Tıklama" value={sayiKisa(t.clicks)}
            lines={[['Google + Meta', nf(gm.clicks)], p.tiktok?.clicks ? ['TikTok', nf(p.tiktok.clicks)] : null]} />
          <Kpi label="CTR (Google + Meta)" value={ctrYazi(gm.ctr)}
            lines={[['Google', ctrYazi(oran(p.google?.clicks, p.google?.impressions))], ['Meta', ctrYazi(oran(p.meta?.clicks, p.meta?.impressions))]]} />
          <Kpi label="Dönüşüm (satın alma)" value={nf1(t.conv)}
            lines={[['Dönüşüm değeri', tl(t.value)], ['Dönüşüm başı maliyet', t.conv ? tl(t.cost / t.conv) : '—']]} />
          <Kpi label="ROAS" value={roasYazi(t.roas)} renk={C.accent}
            lines={[['Google', roasYazi(oran(p.google?.value, p.google?.cost))], ['Meta', roasYazi(oran(p.meta?.value, p.meta?.cost))]]} />
        </div>

        {/* Site satışı + genel kampanyalar şeridi */}
        <Card style={{ marginTop: 14, padding: '18px 24px', opacity: loading ? 0.55 : 1 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 48px', alignItems: 'baseline' }} className="tabular-nums">
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Sitede Zoo Family satışı (GA4)</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{ga4 ? tl(ga4.revenue) : '—'} <span style={{ fontSize: 15, fontWeight: 500, color: C.muted }}>{ga4 ? `· ${nf(ga4.qty)} adet` : ''}</span></div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Reklam harcaması / site satışı</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{ga4?.revenue ? pct(t.cost / ga4.revenue, 1) : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Genel katalog kampanyalarında ek harcama</div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{tl(ekHarcama)}</div>
            </div>
            <div style={{ flex: '1 1 320px', fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
              Site satışı, Zoo Family ürünlerinin hangi kanaldan gelirse gelsin toplam cirosudur; reklam atfı değildir.
              Ek harcama, Zoo Family ürünlerinin "Tüm Ürünler" tipi katalog kampanyalarında aldığı paydır ve yukarıdaki toplama dahil değildir.
            </div>
          </div>
          {data?.ga4Error ? <div style={{ fontSize: 12.5, color: C.down, marginTop: 8 }}>GA4 okunamadı: {data.ga4Error}</div> : null}
        </Card>

        <BolumBaslik eyebrow="Platformlar" title="Google, Meta ve TikTok"
          sub="Zoo Family'ye ayrılmış kampanya, reklam grubu ve adset'lerin platform toplamı." />
        <PlatformTablosu data={data} />

        <BolumBaslik eyebrow="Aylık" title="Ay ay harcama ve sonuç"
          sub="Her ayın harcaması platform kırılımıyla; yanında sitenin o ayki Zoo Family cirosu." />
        <AylikTablo data={data} loading={loading} />

        <BolumBaslik eyebrow="Kampanyalar" title="Hangi kampanya ne getirdi"
          sub="Zoo Family'ye ayrılmış her reklam grubu, PMax varlık grubu ve adset. Başlıklara tıklayarak sıralayın." />
        <KampanyaTablosu data={data} loading={loading} />

        <BolumBaslik eyebrow="Ürünler" title="Reklamlarda gösterilen Zoo Family ürünleri"
          sub="Google alışveriş ve Meta katalog reklamlarında ürün bazında harcama; genel katalog kampanyalarındaki pay dahil." />
        <UrunTablosu data={data} loading={loading} />

        <BolumBaslik eyebrow="Satış" title="Sitede en çok satan Zoo Family ürünleri"
          sub="GA4 e-ticaret: görüntüleme, sepete ekleme, adet ve ciro." />
        <SatisTablosu data={data} loading={loading} />

        <div style={{ fontSize: 12, color: C.faint, lineHeight: 1.6, marginTop: 28, paddingBottom: 24 }}>
          Harcama, gösterim, tıklama ve dönüşüm Google Ads, Meta ve TikTok hesaplarından; site satışı GA4'ten.
          TikTok verisi 1 Eylül 2026'dan, Meta ürün kırılımı 9 Haziran 2026'dan itibaren tutuluyor.
          {data?.cached ? ' · önbellekten' : ''}
        </div>
      </div>
    </div>
  );
}
